<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import { Beneficiary, CustomerTypeEnum, Form, Person } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Beneficiary
  inputsClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  inputsClass: undefined,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const v$ = useValidation(
  () => ({
    name: { required: requiredValidator },
    firstLastName: { required: requiredValidator },
    birthDate: { required: requiredValidator },
    relationship: { required: requiredValidator },
    gender: { required: requiredValidator },
  }),
  model,
  { $scope: Form.beneficiaries, $registerAs: 'beneficiary' }
)
</script>

<template>
  <FormsGlobalContainer v-slot="{ sectionClasses }">
    <FormsGlobalTitle
      text="Beneficiario"
      icon-class="icon-[ph--user-bold]"
    />

    <div :class="[sectionClasses(), 'col-span-full']">
      <FormsIdentificationFields
        v-model="model.identification"
        :validation-scope="Form.beneficiaries"
        ignore-validations
        :entity-type="CustomerTypeEnum.Person.code"
      />
    </div>

    <InputField
      label="Nombre"
      :error="v$.name?.$error"
      :error-message="v$.name?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.name" />
    </InputField>

    <InputField
      label="Primer apellido"
      :error="v$.firstLastName?.$error"
      :error-message="v$.firstLastName?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.firstLastName" />
    </InputField>

    <InputField label="Segundo apellido">
      <PvInputText v-model="model.secondLastName" />
    </InputField>

    <InputField
      label="Fecha de nacimiento"
      :error="v$.birthDate?.$error"
      :error-message="v$.birthDate?.$errors[0]?.$message.toString()"
    >
      <PvDatePicker
        v-model="model.birthDate"
        :max-date="new Date()"
      />
    </InputField>

    <InputField
      label="Parentesco"
      :error="v$.relationship?.$error"
      :error-message="v$.relationship?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.relationship"
        :options="Beneficiary.getRelationshipTypeList()"
      />
    </InputField>

    <InputField
      label="Género"
      :error="v$.gender?.$error"
      :error-message="v$.gender?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.gender"
        option-label="label"
        option-value="code"
        :options="Person.getGenderTypeList(true)"
      />
    </InputField>
  </FormsGlobalContainer>
</template>
